import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/lib/button-variants";
import type { VariantProps } from "class-variance-authority";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";

interface SplitButtonOption {
  label: string;
  value: string;
  icon?: React.ReactNode;
  // Shown but not selectable; hint says why (e.g. "Choose a category first")
  disabled?: boolean;
  hint?: string;
}

interface SplitButtonProps
  extends VariantProps<typeof buttonVariants> {
  label: string;
  onClick: () => void;
  isPending?: boolean;
  pendingLabel?: string;
  options: SplitButtonOption[];
  onOptionSelect: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

function SplitButton({
  label,
  onClick,
  isPending = false,
  pendingLabel,
  options,
  onOptionSelect,
  disabled = false,
  variant = "default",
  size = "default",
  className,
}: SplitButtonProps) {
  return (
    <div className={cn("inline-flex", className)}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled || isPending}
        className={cn(
          buttonVariants({ variant, size }),
          "rounded-r-none border-r-0"
        )}
      >
        {isPending ? (pendingLabel || label) : label}
      </button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled || isPending}
            className={cn(
              buttonVariants({ variant, size }),
              "rounded-l-none px-2 border-l border-l-primary-foreground/20"
            )}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {options.map((option) => (
            <DropdownMenuItem
              key={option.value}
              onClick={() => onOptionSelect(option.value)}
              disabled={option.disabled}
              title={option.hint}
              className="cursor-pointer"
            >
              {option.icon && <span className="mr-2">{option.icon}</span>}
              <span className="flex flex-col">
                <span>{option.label}</span>
                {option.disabled && option.hint && (
                  <span className="text-xs text-muted-foreground">{option.hint}</span>
                )}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export { SplitButton };
export type { SplitButtonOption, SplitButtonProps };
